import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@raro/ui';
import { api } from '../lib/api';

interface MapPoint { id: string; name: string }
interface MapRow { place: { id: string; name: string; uf: string | null; city: string | null }; client: { points: MapPoint[] }; thirdParty: MapPoint[]; competitors: { competitor: { name: string }; points: MapPoint[] }[] }

/**
 * De onde é este material: um ponto (a loja de Assis, não a marca) ou só a praça.
 * Sem vínculo, a fonte vale para a marca inteira. Some quando o cliente ainda não tem praças.
 */
export function SourcePlace({ sourceId, organizationId, placeId, pointId, canEdit }: { sourceId: string; organizationId: string; placeId: string | null; pointId: string | null; canEdit: boolean }) {
  const qc = useQueryClient();
  const map = useQuery({ queryKey: ['places', organizationId], queryFn: () => api.get<{ places: MapRow[] }>(`/organizations/${organizationId}/places`) });
  const save = useMutation({
    mutationFn: (value: string) => api.patch(`/sources/${sourceId}`, value.startsWith('point:') ? { pointId: value.slice(6) } : value.startsWith('place:') ? { pointId: null, placeId: value.slice(6) } : { pointId: null, placeId: null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['source'] }); },
  });
  const rows = map.data?.places ?? [];
  if (rows.length === 0) return null;
  const value = pointId ? `point:${pointId}` : placeId ? `place:${placeId}` : '';
  const label = (r: MapRow) => `${r.place.name}${r.place.city ? `, ${r.place.city}` : ''} · ${r.place.uf ?? ''}`;
  return (
    <Select value={value} onChange={(e) => save.mutate(e.target.value)} disabled={!canEdit || save.isPending} aria-label="de onde é este material" style={{ width: 'auto', maxWidth: 320 }} searchable>
      <option value="">sem praça: vale para a marca</option>
      {rows.map((r) => (
        <optgroup key={r.place.id} label={label(r)}>
          <option value={`place:${r.place.id}`}>{r.place.name} (a praça)</option>
          {r.client.points.map((p) => <option key={p.id} value={`point:${p.id}`}>{p.name}</option>)}
          {r.competitors.flatMap((c) => c.points.map((p) => <option key={p.id} value={`point:${p.id}`}>{p.name} · {c.competitor.name}</option>))}
          {r.thirdParty.map((p) => <option key={p.id} value={`point:${p.id}`}>{p.name}</option>)}
        </optgroup>
      ))}
    </Select>
  );
}
